import { motion } from "framer-motion";
import { Ban, Banknote, BadgeCheck, Scale } from "lucide-react";

const chapters = [
  {
    num: "01",
    icon: Ban,
    title: "Show up or you're banned",
    body: "Accept a shift and fail to arrive? One confirmed no-show means a permanent ban. No appeals, no second chances. That's the deal — and it's why venues trust us.",
    danger: true,
  },
  {
    num: "02",
    icon: Banknote,
    title: "Honest hours, direct pay",
    body: "GPS check-in and checkout. No timesheet disputes, no chasing invoices, no agency skimming your rate — the venue pays you directly at the agreed rate. Every hour you work is yours.",
  },
  {
    num: "03",
    icon: BadgeCheck,
    title: "Verified or nothing",
    body: "SIA licence and background check are verified against the register before your first shift. No paperwork games, no unvetted guards on site. Ever.",
  },
  {
    num: "04",
    icon: Scale,
    title: "Rated both ways",
    body: "Guards rate venues on safety and fair treatment. Venues rate guards on punctuality and professionalism. Consistently low ratings lose platform access — on both sides.",
  },
];

export default function Manifesto() {
  return (
    <section id="rules" data-testid="rules-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16 max-w-3xl"
        >
          <p className="font-mono-tag text-[11px] text-amber-500">The GuardGig28 Rules</p>
          <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
            Serious about work.
            <span className="text-stroke-amber block">Fair about everything else.</span>
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">
            Four non-negotiable chapters. They protect guards from bad venues and venues
            from unreliable guards. Read them before you download — we enforce every word.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {chapters.map((c, i) => (
            <motion.article
              key={c.num}
              data-testid={`rule-chapter-${c.num}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              className={`group relative flex flex-col rounded-xl border p-7 transition-colors duration-300 ${
                c.danger
                  ? "border-red-500/30 bg-gradient-to-b from-red-500/5 to-transparent"
                  : "border-white/8 bg-[#0f141c] hover:border-amber-500/30"
              }`}
            >
              <div className="flex items-start justify-between">
                <span className={`font-display text-5xl font-black ${c.danger ? "text-red-500/25" : "text-white/8"} transition-colors duration-300 group-hover:text-amber-500/30`}>
                  {c.num}
                </span>
                <c.icon className={`h-6 w-6 ${c.danger ? "text-red-500" : "text-amber-500"}`} strokeWidth={1.75} />
              </div>
              <h3 className="font-display mt-8 text-xl font-bold uppercase leading-tight text-slate-50">
                {c.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.body}</p>
              {c.danger && (
                <span className="mt-5 inline-flex w-fit items-center rounded border border-red-500/40 bg-red-500/10 px-2.5 py-1 font-mono-tag text-[9px] text-red-400">
                  Zero-tolerance policy
                </span>
              )}
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
